import React from "react"
import { Sparkles } from "lucide-react"
import { useTranslation } from "react-i18next"
import { useScrollAnimation } from "../hooks/useScrollAnimation"

export default function ProductOverview() {
    const { t } = useTranslation()
    const ref = useScrollAnimation()

    const features = [
        t('productOverview.feature1'),
        t('productOverview.feature2'),
        t('productOverview.feature3'),
    ]

    return (
        <section id="product-overview" ref={ref} className="scroll-animate py-24 bg-gradient-to-br from-slate-950 via-blue-950 to-emerald-950 relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
                <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-500 rounded-full blur-[120px] -mr-64 -mt-64 text-white"></div>
                <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-500 rounded-full blur-[120px] -ml-64 -mb-64"></div>
            </div>

            <div className="container mx-auto px-4 md:px-6 relative z-10">
                <div className="text-center max-w-4xl mx-auto">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 text-emerald-500 text-sm font-bold mb-8 ring-1 ring-emerald-500/20 backdrop-blur-sm">
                        <Sparkles className="w-4 h-4 animate-pulse" />
                        <span className="uppercase tracking-widest">{t('productOverview.title')}</span>
                    </div>

                    {/* Title */}
                    <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight drop-shadow-lg">
                        {t('productOverview.subtitle')}
                    </h2>

                    {/* Description */}
                    <p className="text-xl text-slate-400 mb-12 leading-relaxed max-w-2xl mx-auto">
                        {t('productOverview.description')}
                    </p>

                    {/* Card Container */}
                    <div className="bg-slate-900/50 backdrop-blur-xl rounded-[2.5rem] shadow-2xl p-8 md:p-14 border border-white/10 relative group">
                        <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 to-transparent rounded-[2.5rem] opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>

                        {/* Features List */}
                        <div className="grid sm:grid-cols-3 gap-6 mb-12 relative z-10">
                            {features.map((feature, index) => (
                                <div
                                    key={index}
                                    className={`flex flex-col items-center gap-4 p-8 rounded-3xl bg-white/5 border border-white/5 hover:border-emerald-500/30 hover:bg-white/10 transition-all duration-500 ${index === 0 ? 'slide-top' :
                                        index === 1 ? 'slide-left-in' :
                                            'slide-right-in'
                                        }`}
                                >
                                    <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center border border-emerald-500/20 mb-2">
                                        <div className="w-3 h-3 rounded-full bg-emerald-500 shadow-[0_0_15px_rgba(16,185,129,0.8)]"></div>
                                    </div>
                                    <span className="text-lg text-white font-semibold text-center">{feature}</span>
                                </div>
                            ))}
                        </div>

                        {/* Footer */}
                        <div className="pt-8 border-t border-white/10 relative z-10">
                            <p className="text-emerald-500 font-bold tracking-widest uppercase text-sm">
                                {t('productOverview.footer')}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}
